/**
 * DI CHI E' IL TURNO — e soprattutto: **CHE COSA deve fare**.
 *
 * [Luca, 2026-09-14] «*quando devo lanciare i dadi perche' il mio avversario ha fatto la mossa
 * vedo in notifica che e' opponents turn, quando invece devo fare roll*».
 *
 * ═══ 🔴 IL DIFETTO, e perche' era invisibile ═══
 * `lib/activeMatch/projection.ts` diceva:
 *     `game.turn === perspective && game.phase === 'moving'`
 * ⇒ in fase **`rolling`** — cioe' quando tocca a me **tirare** — la condizione e' falsa e la
 * pastiglia annuncia «turno dell'avversario». ⭐ *Il predicato non era sbagliato: era piu'
 * STRETTO di cio' che il suo nome prometteva.* «E' il mio turno?» era implementato come «sto
 * muovendo?», e i due coincidono in tutti i casi tranne quello in cui l'utente ha bisogno
 * dell'avviso — perche' ha appena chiuso l'app aspettando l'avversario.
 *
 * ═══ ⚠️ E NON BASTA AGGIUNGERE `rolling`: c'e' un terzo caso, ed e' ROVESCIATO ═══
 * In **`cubeOffered`** agisce **chi NON ha il turno**: il raddoppio lo offre chi ha il turno, e a
 * dover rispondere e' l'altro. La rotta lo sapeva gia'
 * (`BackgammonMatch.tsx`: `iAmCubeReceiver = cubeOffered && turn !== perspective`), ma quella
 * regola viveva **solo li'** — e la pastiglia, che e' l'unica superficie visibile a chi e' USCITO
 * dalla partita, non la conosceva.
 * ⭐ *Una regola che vive dentro la schermata che la usa non e' condivisa: e' nascosta.*
 */
/** Il colore, come lo scrive il server. ⚠️ Il pacchetto dichiara `Player` ma **non lo esporta**:
 *  si ridichiara qui invece di importarlo da un percorso interno, che si romperebbe al primo
 *  riassetto del pacchetto senza che niente lo annunci. */
type Player = 'W' | 'B';

/** La forma minima di stato che serve a decidere. Volutamente NON il payload intero: cosi' il
 *  modulo resta puro e provabile senza montare niente. */
export interface StatoPerTurno {
  readonly turn: Player | null | undefined;
  readonly phase: string | null | undefined;
}

/**
 * Che cosa deve fare il giocatore `io`, se deve fare qualcosa.
 * · `'roll'`  — tirare i dadi (fase `rolling`, turno suo);
 * · `'move'`  — muovere (fase `moving`, turno suo);
 * · `'cube'`  — rispondere a un raddoppio (fase `cubeOffered`, turno **dell'altro**);
 * · `null`    — non tocca a lui, **oppure non lo sappiamo**.
 *
 * ⛔ `null` NON vuol dire «tocca all'avversario»: vuol dire «non tocca a me, per quel che so».
 * Su una fase che questo modulo non conosce si tace, invece di affermare il contrario — un
 * avviso sbagliato su una partita a tempo costa un turno.
 */
export function azioneAttesa(
  stato: StatoPerTurno | null | undefined,
  io: Player | null | undefined,
): 'roll' | 'move' | 'cube' | null {
  if (!stato || !io) return null;
  const { turn, phase } = stato;
  if (!turn || !phase) return null;
  if (phase === 'cubeOffered') {
    // 🔴 ROVESCIATO, ed e' l'unico caso: offre chi ha il turno, risponde l'altro.
    return turn !== io ? 'cube' : null;
  }
  if (turn !== io) return null;
  if (phase === 'rolling') return 'roll';
  if (phase === 'moving') return 'move';
  // Fase sconosciuta (o futura): si tace. ⭐ Un modulo che indovina su un valore che non conosce
  // trasforma un'aggiunta al contratto in un difetto silenzioso.
  return null;
}

/** Scorciatoia: tocca a me fare qualcosa? */
export function toccaAMe(
  stato: StatoPerTurno | null | undefined,
  io: Player | null | undefined,
): boolean {
  return azioneAttesa(stato, io) !== null;
}
